/**
 * DragToMove Manager
 *
 * Provides two drag interactions for moving a TransformNode:
 *
 * 1. **Camera-plane drag** — Pointer-down on the object's meshes drags it
 *    along a plane perpendicular to the camera's forward vector, passing
 *    through the object's world position at drag start.
 *
 * 2. **Ground circle** — A draggable disc at Y=0 below the object, connected
 *    by a vertical line. Dragging the disc moves the object in XZ only, so
 *    it is offered only while `axes` allows X or Z.
 *
 * Used by:
 * - `Moveable` behavior at runtime (viewer)
 * - Planar drag service in the editor (sandbox)
 */

import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { IPointerEvent } from "@babylonjs/core/Events/deviceInputEvents";
import { Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import type { PointerInfo } from "@babylonjs/core/Events/pointerEvents";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Observable } from "@babylonjs/core/Misc/observable";
import { UtilityLayerRenderer } from "@babylonjs/core/Rendering/utilityLayerRenderer";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import type { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Axis } from "../scenedata/common";
import { VelocityTracker, glideSweep, glideRate } from "../utils/dynamics";

// ============================================================================
// Types
// ============================================================================

export interface DragToMoveOptions {
    /** Allowed movement axes. When omitted, all axes are allowed. */
    axes?: Axis[];
    /**
     * Whether to show the ground-plane circle. Default: true. The circle only
     * moves the target in XZ, so it is never shown while `axes` allows
     * neither X nor Z.
     */
    groundCircle?: boolean;
    /** Called when a drag starts (before onDragStartObservable). Use to select the object in the editor. */
    onGrab?: () => void;
    /** Enable snap support (Ctrl/Alt = fine snap, Ctrl/Alt+Shift = coarse snap). Default: false. */
    snap?: boolean;
    /**
     * Release momentum: slide-out time constant in seconds. On release the
     * flick keeps sliding, decaying per the closed-form kernel in
     * utils/dynamics (spec: docs-internal/MANIPULATION_MOMENTUM_SPEC.md).
     * 0 or omitted = stops dead on release (classic behavior). This is a
     * manager OPTION, never read from behavior data here — the editor's
     * drag instances capture the pose at drag-end and must never glide.
     */
    momentumDamping?: number;
}

// ============================================================================
// Constants
// ============================================================================

const CIRCLE_RADIUS = 0.15;
const CIRCLE_COLOR = Color3.FromHexString("#0984e3");
const CIRCLE_ALPHA = 0.35;
const CIRCLE_HOVER_ALPHA = 0.6;
const LINE_COLOR = new Color4(0.035, 0.518, 0.890, 0.5);

// Momentum constants — load-bearing spec values, mirrored in
// docs-internal/MANIPULATION_MOMENTUM_SPEC.md. Do not tune casually.
/** Glide stops when the speed falls below this (m/s). */
const MOMENTUM_STOP_SPEED = 0.01;
/** Release-velocity clamp (m/s) — tames single-frame pointer spikes. */
const MOMENTUM_MAX_SPEED = 10;

// Snap constants (matching gizmo-snap-service)
const SNAP_POSITION_FINE = 0.1;
const SNAP_POSITION_COARSE = 0.5;

function roundTo(value: number, step: number): number {
    return Math.round(value / step) * step;
}

/**
 * Whether the ground circle can move a target locked to `axes`. The circle
 * drags on the Y=0 plane, so its motion is X/Z only; with neither allowed it
 * would take the press and move nothing. Omitted or empty axes allow every
 * axis, the same rule `_applyDelta` applies.
 */
function groundCircleCanMove(axes: Axis[] | undefined): boolean {
    if (!axes || axes.length === 0) return true;
    return axes.includes("x") || axes.includes("z");
}

// ============================================================================
// DragToMove
// ============================================================================

/**
 * Module-level guard: only one DragToMove can be actively dragging at a time.
 * Prevents multiple instances from claiming the same pointer event when
 * SceneObjects are in a parent-child hierarchy.
 */
let _activeDrag: DragToMove | null = null;

/**
 * Registry of all enabled DragToMove targets. Used by `_isMeshOfTarget` to
 * find the most specific (nearest ancestor) target for a picked mesh,
 * preventing a parent SceneObject from claiming clicks on child SceneObjects.
 */
const _allTargets = new Map<TransformNode, DragToMove>();

export class DragToMove {
    /** Fires when any drag interaction begins. */
    public readonly onDragStartObservable = new Observable<void>();
    /** Fires when any drag interaction ends. */
    public readonly onDragEndObservable = new Observable<void>();

    private readonly _target: TransformNode;
    private readonly _scene: Scene;
    private _options: DragToMoveOptions;
    private _enabled = false;
    private _disposed = false;

    // Pointer tracking
    private _pointerObserver: Observer<PointerInfo> | null = null;
    private _utilPointerObserver: Observer<PointerInfo> | null = null;
    private _isDragging = false;
    private _isDraggingCircle = false;
    private _dragPlane: Plane | null = null;
    private _dragStartWorldPos: Vector3 | null = null;
    private _dragStartNodePos: Vector3 | null = null;
    private _dragPointerId: number | null = null;

    // Cmd+drag XZ constraint: when Meta is held during camera-plane drag,
    // we switch to an XZ plane through the object's Y position.
    private _cmdDragXZPlane: Plane | null = null;
    private _cmdDragActive = false;

    // Snap accumulator: tracks unsnapped delta so small moves accumulate
    private _snapAccumulator: Vector3 = Vector3.Zero();

    // Release momentum: per-channel velocity estimation over the target's
    // parent-local position (what the drag writes — axis constraints and
    // plane projection are already baked into the samples), plus the frozen
    // glide snapshot after release. Local space involves no camera, so
    // nothing can bend the slide-out.
    private _velPX = new VelocityTracker();
    private _velPY = new VelocityTracker();
    private _velPZ = new VelocityTracker();
    private _glideObserver: Observer<Scene> | null = null;
    private _glide: {
        startPos: Vector3;
        v: Vector3;
        tau: number;
        startTime: number;
    } | null = null;

    // Ground circle visuals
    private _utilityLayer: UtilityLayerRenderer | null = null;
    private _circleDisc: Mesh | null = null;
    private _circleRing: Mesh | null = null;
    private _connectorLine: LinesMesh | null = null;
    private _circleMaterial: StandardMaterial | null = null;
    private _circleHoverMaterial: StandardMaterial | null = null;
    private _beforeRenderObserver: Observer<Scene> | null = null;
    private _circleHovered = false;

    constructor(target: TransformNode, scene: Scene, options?: DragToMoveOptions) {
        this._target = target;
        this._scene = scene;
        this._options = {
            groundCircle: true,
            ...options,
        };
    }

    // ── Public API ──

    get enabled(): boolean { return this._enabled; }

    set enabled(value: boolean) {
        if (this._disposed || value === this._enabled) return;
        this._enabled = value;
        if (value) {
            this._activate();
        } else {
            this._deactivate();
        }
    }

    /** Update options at runtime (e.g. when axes change via commit). */
    public setOptions(options: Partial<DragToMoveOptions>): void {
        this._options = { ...this._options, ...options };
        // Recreate the ground circle: the flag or the axes may have changed
        if (this._enabled) {
            this._teardownGroundCircle();
            if (this._showsGroundCircle()) {
                this._setupGroundCircle();
            }
        }
    }

    public dispose(): void {
        if (this._disposed) return;
        this._disposed = true;
        this._enabled = false;
        this._deactivate();
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
    }

    // ── Activation / deactivation ──

    private _activate(): void {
        _allTargets.set(this._target, this);
        this._setupPointerObserver();
        if (this._showsGroundCircle()) {
            this._setupGroundCircle();
        }
    }

    /** The circle is wanted and can move the target on its allowed axes. */
    private _showsGroundCircle(): boolean {
        return this._options.groundCircle !== false && groundCircleCanMove(this._options.axes);
    }

    private _deactivate(): void {
        _allTargets.delete(this._target);
        this._endDrag();
        // Disable (play-mode exit / dispose) stops any glide — including one
        // _endDrag just started from a mid-drag deactivation
        this._cancelGlide();
        this._teardownPointerObserver();
        this._teardownGroundCircle();
    }

    // ── Main scene pointer observer (camera-plane drag on target meshes) ──

    private _setupPointerObserver(): void {
        if (this._pointerObserver) return;
        this._pointerObserver = this._scene.onPointerObservable.add((pi) => {
            switch (pi.type) {
                case PointerEventTypes.POINTERDOWN: {
                    if (pi.event.button !== 0) return; // Only left-click — right-click is for camera panning
                    if (this._isDragging || _activeDrag) return;
                    const pick = pi.pickInfo;
                    if (!pick?.hit || !pick.pickedMesh) return;
                    if (this._isMeshOfTarget(pick.pickedMesh)) {
                        this._startCameraPlaneDrag(pi);
                    }
                    break;
                }
                case PointerEventTypes.POINTERMOVE: {
                    if (!this._isDragging || this._isDraggingCircle) return;
                    if (this._getPointerId(pi) !== this._dragPointerId) return;
                    this._updateCameraPlaneDrag(pi);
                    break;
                }
                case PointerEventTypes.POINTERUP: {
                    if (!this._isDragging) return;
                    if (this._getPointerId(pi) !== this._dragPointerId) return;
                    this._endDrag();
                    break;
                }
            }
        });
    }

    private _teardownPointerObserver(): void {
        if (this._pointerObserver) {
            this._scene.onPointerObservable.remove(this._pointerObserver);
            this._pointerObserver = null;
        }
        if (this._utilPointerObserver && this._utilityLayer) {
            this._utilityLayer.utilityLayerScene.onPointerObservable.remove(this._utilPointerObserver);
            this._utilPointerObserver = null;
        }
    }

    /** Extract pointerId safely — IMouseEvent may not have it, but IPointerEvent does. */
    private _getPointerId(pi: PointerInfo): number {
        return (pi.event as IPointerEvent).pointerId ?? 0;
    }

    // ── Camera-plane drag ──

    private _startCameraPlaneDrag(pi: PointerInfo): void {
        if (_activeDrag && _activeDrag !== this) return;
        const camera = this._scene.activeCamera;
        if (!camera) return;

        // Lock the drag plane: perpendicular to camera forward, through the object
        const cameraForward = camera.getForwardRay().direction.normalize();
        const objectWorldPos = this._target.getAbsolutePosition();
        this._dragPlane = Plane.FromPositionAndNormal(objectWorldPos, cameraForward);
        this._isDraggingCircle = false;

        // Pre-compute the XZ plane for Cmd+drag constraint
        this._cmdDragXZPlane = Plane.FromPositionAndNormal(
            new Vector3(0, objectWorldPos.y, 0),
            Vector3.Up(),
        );

        // Check if Cmd is already held at drag start
        this._cmdDragActive = pi.event.metaKey;
        const pickPlane = this._cmdDragActive ? this._cmdDragXZPlane : this._dragPlane;

        // Compute the initial world hit point on the active drag plane
        const hitPoint = this._pickOnPlane(pi, pickPlane);
        if (!hitPoint) return;

        this._dragStartWorldPos = hitPoint;
        this._dragStartNodePos = this._target.position.clone();
        this._resetMomentumTracking();
        this._dragPointerId = this._getPointerId(pi);
        this._isDragging = true;
        this._snapAccumulator.setAll(0);
        _activeDrag = this;

        this._detachCamera();
        this._options.onGrab?.();
        this.onDragStartObservable.notifyObservers();
    }

    private _updateCameraPlaneDrag(pi: PointerInfo): void {
        if (!this._dragPlane || !this._dragStartWorldPos || !this._dragStartNodePos) return;

        // Check Cmd (Meta) state for XZ constraint toggle
        const wantXZ = pi.event.metaKey;
        if (wantXZ !== this._cmdDragActive) {
            // Mode changed mid-drag — reset start position to avoid jump
            this._cmdDragActive = wantXZ;
            const plane = wantXZ ? this._cmdDragXZPlane : this._dragPlane;
            const newStart = this._pickOnPlane(pi, plane);
            if (newStart) {
                this._dragStartWorldPos = newStart;
                this._dragStartNodePos = this._target.position.clone();
                this._resetMomentumTracking();
                this._snapAccumulator.setAll(0);
            }
        }

        const activePlane = wantXZ ? this._cmdDragXZPlane : this._dragPlane;
        const currentHit = this._pickOnPlane(pi, activePlane);
        if (!currentHit) return;

        const worldDelta = currentHit.subtract(this._dragStartWorldPos);
        if (wantXZ) {
            worldDelta.y = 0;
        } else {
            // Hybrid mode: decompose camera-plane delta into
            // screen-horizontal → camera-right projected onto XZ
            // screen-vertical → world Y
            this._remapToHybridDelta(worldDelta);
        }
        this._applyDelta(worldDelta, pi);
    }

    /**
     * Remap a camera-plane world delta so that horizontal mouse movement
     * maps to camera-right in XZ and vertical mouse movement maps to world Y.
     * Modifies the vector in place.
     */
    private _remapToHybridDelta(delta: Vector3): void {
        const camera = this._scene.activeCamera;
        if (!camera) return;

        // Camera right and up from the view matrix
        const camWorld = camera.getWorldMatrix();
        const cameraRight = Vector3.TransformNormal(Vector3.Right(), camWorld);
        const cameraUp = Vector3.TransformNormal(Vector3.Up(), camWorld);
        cameraRight.normalize();
        cameraUp.normalize();

        // Decompose the world-space delta into screen-space components
        const horizontalAmount = Vector3.Dot(delta, cameraRight);
        const verticalAmount = Vector3.Dot(delta, cameraUp);

        // Project camera right onto XZ plane
        const cameraRightXZ = new Vector3(cameraRight.x, 0, cameraRight.z);
        const len = cameraRightXZ.length();
        if (len > 0.001) {
            cameraRightXZ.scaleInPlace(1 / len);
        } else {
            // Camera looking straight down/up — fall back to world X
            cameraRightXZ.set(1, 0, 0);
        }

        // Remap: horizontal screen drag → camera-right in XZ, vertical → world Y
        delta.set(
            cameraRightXZ.x * horizontalAmount,
            verticalAmount,
            cameraRightXZ.z * horizontalAmount
        );
    }

    // ── Ground circle drag ──

    private _startGroundCircleDrag(pi: PointerInfo): void {
        if (_activeDrag && _activeDrag !== this) return;
        // Drag plane is the XZ ground plane (Y=0)
        this._dragPlane = Plane.FromPositionAndNormal(Vector3.Zero(), Vector3.Up());
        this._isDraggingCircle = true;

        const hitPoint = this._pickOnPlane(pi);
        if (!hitPoint) return;

        this._dragStartWorldPos = hitPoint;
        this._dragStartNodePos = this._target.position.clone();
        this._resetMomentumTracking();
        this._dragPointerId = this._getPointerId(pi);
        this._isDragging = true;
        this._snapAccumulator.setAll(0);
        _activeDrag = this;

        this._detachCamera();
        this._options.onGrab?.();
        this.onDragStartObservable.notifyObservers();
    }

    private _updateGroundCircleDrag(pi: PointerInfo): void {
        if (!this._dragPlane || !this._dragStartWorldPos || !this._dragStartNodePos) return;

        const currentHit = this._pickOnPlane(pi);
        if (!currentHit) return;

        // Ground circle drag only moves in XZ
        const worldDelta = currentHit.subtract(this._dragStartWorldPos);
        worldDelta.y = 0;
        this._applyDelta(worldDelta, pi);
    }

    // ── Shared drag helpers ──

    /** Check if snap modifier is active (Ctrl on Mac, Alt on Mac — same as gizmo snap). */
    private _isSnapActive(pi: PointerInfo): boolean {
        if (!this._options.snap) return false;
        return pi.event.altKey;
    }

    /**
     * Apply a world-space delta to the target, respecting axis constraints,
     * parent transforms, and optional snapping.
     */
    private _applyDelta(worldDelta: Vector3, pi?: PointerInfo): void {
        // Apply axis constraints
        const axes = this._options.axes;
        if (axes && axes.length > 0) {
            if (!axes.includes("x")) worldDelta.x = 0;
            if (!axes.includes("y")) worldDelta.y = 0;
            if (!axes.includes("z")) worldDelta.z = 0;
        }

        // Convert world delta to local space if the target has a parent
        let localDelta = worldDelta;
        const parent = this._target.parent as TransformNode | null;
        if (parent) {
            const parentWorldMatrix = parent.getWorldMatrix();
            const invParent = Matrix.Invert(parentWorldMatrix);
            localDelta = Vector3.TransformNormal(worldDelta, invParent);
        }

        // Apply snap if modifier is held
        if (pi && this._options.snap && this._isSnapActive(pi)) {
            const ev = pi.event;
            const step = ev.shiftKey ? SNAP_POSITION_COARSE : SNAP_POSITION_FINE;

            // Accumulator pattern: add raw delta, snap the accumulated total
            const rawPos = this._dragStartNodePos!.add(localDelta);
            this._snapAccumulator.copyFrom(rawPos);
            const snappedPos = new Vector3(
                roundTo(this._snapAccumulator.x, step),
                roundTo(this._snapAccumulator.y, step),
                roundTo(this._snapAccumulator.z, step),
            );
            this._target.position = snappedPos;
        } else {
            this._target.position = this._dragStartNodePos!.add(localDelta);
        }

        // Feed the momentum estimators (wall clock, same timebase as the
        // glide). Skipped entirely when momentum is off — editor drag
        // instances never read the trackers.
        if (this._momentumEnabled()) {
            const sampleTime = performance.now();
            this._velPX.push(this._target.position.x, sampleTime);
            this._velPY.push(this._target.position.y, sampleTime);
            this._velPZ.push(this._target.position.z, sampleTime);
        }
    }

    /**
     * Pick a point on a plane from the pointer event.
     * Defaults to `this._dragPlane` if no explicit plane is given.
     * Uses the pick ray from the event when available (XR controllers),
     * otherwise reconstructs from screen coordinates.
     */
    private _pickOnPlane(pi: PointerInfo, plane?: Plane | null): Vector3 | null {
        const p = plane ?? this._dragPlane;
        const camera = this._scene.activeCamera;
        if (!camera || !p) return null;

        // Prefer the ray from pickInfo (works in XR where screen coords are meaningless)
        const ray = pi.pickInfo?.ray ?? this._scene.createPickingRay(
            pi.event.offsetX,
            pi.event.offsetY,
            Matrix.Identity(),
            camera,
        );

        // Intersect ray with the drag plane
        const distance = ray.intersectsPlane(p);
        if (distance === null || distance < 0) return null;

        return ray.origin.add(ray.direction.scale(distance));
    }

    private _endDrag(): void {
        if (!this._isDragging) return;
        // Snapshot for momentum BEFORE the drag state is torn down
        this._maybeStartGlide();
        this._isDragging = false;
        _activeDrag = null;
        this._isDraggingCircle = false;
        this._dragPlane = null;
        this._dragStartWorldPos = null;
        this._dragStartNodePos = null;
        this._dragPointerId = null;
        this._cmdDragXZPlane = null;
        this._cmdDragActive = false;
        this._snapAccumulator.setAll(0);
        this._reattachCamera();
        this.onDragEndObservable.notifyObservers();
    }

    // ── Release momentum (glide) ──

    /**
     * Whether the momentum option is on: a finite, positive time constant.
     * The finite check matters — NaN/Infinity would NaN the glide's closed
     * form and its stop condition would never fire (NaN <= 0 is false).
     */
    private _momentumEnabled(): boolean {
        const tau = this._options.momentumDamping ?? 0;
        return Number.isFinite(tau) && tau > 0;
    }

    /** Reset velocity estimation for a fresh drag; a re-grab claims the object mid-glide. */
    private _resetMomentumTracking(): void {
        this._cancelGlide();
        this._velPX.reset();
        this._velPY.reset();
        this._velPZ.reset();
        if (!this._momentumEnabled()) return;
        const now = performance.now();
        this._velPX.push(this._target.position.x, now);
        this._velPY.push(this._target.position.y, now);
        this._velPZ.push(this._target.position.z, now);
    }

    /** Read the release velocity and start a glide if it warrants one. */
    private _maybeStartGlide(): void {
        if (!this._momentumEnabled()) return;
        const now = performance.now();
        const v = new Vector3(
            this._velPX.velocity(now),
            this._velPY.velocity(now),
            this._velPZ.velocity(now),
        );
        const speed = v.length();
        if (speed < MOMENTUM_STOP_SPEED) return;
        if (speed > MOMENTUM_MAX_SPEED) v.scaleInPlace(MOMENTUM_MAX_SPEED / speed);
        this._startGlideWithVelocity(v, now);
    }

    /**
     * Begin the slide-out from the current position with the given
     * parent-local velocity (units/s). The position at time t is evaluated in
     * closed form from total elapsed time (never integrated per frame):
     * identical trajectories at any frame rate. Axis constraints and plane
     * projection are already baked into the velocity — it was measured from
     * the drag's own applied motion.
     */
    private _startGlideWithVelocity(v: Vector3, nowMs: number): void {
        this._cancelGlide();
        this._glide = {
            startPos: this._target.position.clone(),
            v: v.clone(),
            tau: this._options.momentumDamping ?? 0,
            startTime: nowMs,
        };
        this._glideObserver = this._scene.onBeforeRenderObservable.add(() => this._glideStep());
    }

    private _glideStep(): void {
        const g = this._glide;
        if (!g) return;
        const t = (performance.now() - g.startTime) / 1000;

        const speed = Math.abs(glideRate(g.v.length(), g.tau, t));

        this._target.position.set(
            g.startPos.x + glideSweep(g.v.x, g.tau, t),
            g.startPos.y + glideSweep(g.v.y, g.tau, t),
            g.startPos.z + glideSweep(g.v.z, g.tau, t),
        );

        if (speed < MOMENTUM_STOP_SPEED) {
            this._cancelGlide();
        }
    }

    /** Stop any running glide. Safe to call at any time. */
    private _cancelGlide(): void {
        if (this._glideObserver) {
            this._scene.onBeforeRenderObservable.remove(this._glideObserver);
            this._glideObserver = null;
        }
        this._glide = null;
    }

    // ── Camera detach / reattach ──

    private _detachCamera(): void {
        const camera = this._scene.activeCamera;
        if (camera) {
            camera.detachControl();
        }
    }

    private _reattachCamera(): void {
        const camera = this._scene.activeCamera;
        const canvas = this._scene.getEngine().getRenderingCanvas();
        if (camera && canvas) {
            camera.attachControl(canvas, true);
        }
    }

    // ── Mesh ownership detection ──

    /**
     * Check if a picked mesh belongs to our target TransformNode.
     * Walks up from the mesh to find the nearest registered DragToMove target,
     * matching ClickToSelect's logic to pick the most specific SceneObject.
     */
    private _isMeshOfTarget(mesh: AbstractMesh): boolean {
        let current: TransformNode | null = mesh;
        while (current) {
            if (_allTargets.has(current)) {
                return current === this._target;
            }
            current = current.parent as TransformNode | null;
        }
        return false;
    }

    /** Check if a picked mesh is part of the ground circle. */
    private _isGroundCircleMesh(mesh: AbstractMesh): boolean {
        return mesh === this._circleDisc || mesh === this._circleRing;
    }

    // ── Ground circle visuals ──

    private _setupGroundCircle(): void {
        if (this._circleDisc) return; // Already set up

        // Use a utility layer so the circle doesn't interfere with scene picking
        // for selection, but is still pickable itself.
        this._utilityLayer = new UtilityLayerRenderer(this._scene);
        this._utilityLayer.utilityLayerScene.autoClearDepthAndStencil = false;
        const utilScene = this._utilityLayer.utilityLayerScene;

        // -- Pointer observer on the utility layer scene for circle picking --
        this._utilPointerObserver = utilScene.onPointerObservable.add((pi) => {
            switch (pi.type) {
                case PointerEventTypes.POINTERDOWN: {
                    if (pi.event.button !== 0) return; // Only left-click
                    if (this._isDragging || _activeDrag) return;
                    const pick = pi.pickInfo;
                    if (!pick?.hit || !pick.pickedMesh) return;
                    if (this._isGroundCircleMesh(pick.pickedMesh)) {
                        this._startGroundCircleDrag(pi);
                    }
                    break;
                }
                case PointerEventTypes.POINTERMOVE: {
                    if (this._isDragging) {
                        if (!this._isDraggingCircle) return;
                        if (this._getPointerId(pi) !== this._dragPointerId) return;
                        this._updateGroundCircleDrag(pi);
                    } else {
                        // Hover tracking
                        const pick = pi.pickInfo;
                        const isHover = !!(pick?.hit && pick.pickedMesh && this._isGroundCircleMesh(pick.pickedMesh));
                        if (isHover !== this._circleHovered) {
                            this._circleHovered = isHover;
                            this._updateCircleHoverState();
                        }
                    }
                    break;
                }
                case PointerEventTypes.POINTERUP: {
                    if (!this._isDragging || !this._isDraggingCircle) return;
                    if (this._getPointerId(pi) !== this._dragPointerId) return;
                    this._endDrag();
                    break;
                }
            }
        });

        // -- Materials --
        this._circleMaterial = new StandardMaterial("dragToMove_circleMat", utilScene);
        this._circleMaterial.diffuseColor = CIRCLE_COLOR;
        this._circleMaterial.alpha = CIRCLE_ALPHA;
        this._circleMaterial.backFaceCulling = false;
        this._circleMaterial.disableLighting = true;
        this._circleMaterial.emissiveColor = CIRCLE_COLOR;

        this._circleHoverMaterial = new StandardMaterial("dragToMove_circleHoverMat", utilScene);
        this._circleHoverMaterial.diffuseColor = CIRCLE_COLOR;
        this._circleHoverMaterial.alpha = CIRCLE_HOVER_ALPHA;
        this._circleHoverMaterial.backFaceCulling = false;
        this._circleHoverMaterial.disableLighting = true;
        this._circleHoverMaterial.emissiveColor = CIRCLE_COLOR;

        // -- Disc (filled circle on ground) --
        this._circleDisc = CreateDisc("dragToMove_disc", {
            radius: CIRCLE_RADIUS,
            tessellation: 48,
        }, utilScene);
        this._circleDisc.material = this._circleMaterial;
        this._circleDisc.isPickable = true;
        // Rotate to lie flat on XZ plane (disc is created in XY)
        this._circleDisc.rotation.x = Math.PI / 2;

        // -- Ring (torus outline for visibility) --
        this._circleRing = CreateTorus("dragToMove_ring", {
            diameter: CIRCLE_RADIUS * 2,
            thickness: 0.005,
            tessellation: 48,
        }, utilScene);
        this._circleRing.material = this._circleMaterial;
        this._circleRing.isPickable = true;

        // -- Connector line --
        this._updateGroundCirclePositions();
        this._createConnectorLine(utilScene);

        // Update positions every frame
        this._beforeRenderObserver = this._scene.onBeforeRenderObservable.add(() => {
            this._updateGroundCirclePositions();
        });
    }

    private _createConnectorLine(utilScene: Scene): void {
        const objPos = this._target.getAbsolutePosition();
        const groundPos = new Vector3(objPos.x, 0, objPos.z);

        this._connectorLine = CreateLines("dragToMove_connector", {
            points: [objPos, groundPos],
            updatable: true,
        }, utilScene);
        this._connectorLine.color = new Color3(LINE_COLOR.r, LINE_COLOR.g, LINE_COLOR.b);
        this._connectorLine.alpha = LINE_COLOR.a;
        this._connectorLine.isPickable = false;
    }

    private _updateGroundCirclePositions(): void {
        const objPos = this._target.getAbsolutePosition();
        const groundPos = new Vector3(objPos.x, 0.001, objPos.z); // Slight offset to avoid z-fighting

        if (this._circleDisc) {
            this._circleDisc.position.copyFrom(groundPos);
        }
        if (this._circleRing) {
            this._circleRing.position.set(groundPos.x, 0.002, groundPos.z);
        }

        // Update connector line
        if (this._connectorLine) {
            this._connectorLine = CreateLines("dragToMove_connector", {
                points: [objPos, groundPos],
                instance: this._connectorLine,
            });
        }
    }

    private _updateCircleHoverState(): void {
        const mat = this._circleHovered ? this._circleHoverMaterial : this._circleMaterial;
        if (this._circleDisc && mat) {
            this._circleDisc.material = mat;
        }
        if (this._circleRing && mat) {
            this._circleRing.material = mat;
        }

        // Change cursor
        const canvas = this._scene.getEngine().getRenderingCanvas();
        if (canvas) {
            canvas.style.cursor = this._circleHovered ? "grab" : "";
        }
    }

    private _teardownGroundCircle(): void {
        if (this._beforeRenderObserver) {
            this._scene.onBeforeRenderObservable.remove(this._beforeRenderObserver);
            this._beforeRenderObserver = null;
        }

        if (this._utilPointerObserver && this._utilityLayer) {
            this._utilityLayer.utilityLayerScene.onPointerObservable.remove(this._utilPointerObserver);
            this._utilPointerObserver = null;
        }

        this._connectorLine?.dispose();
        this._connectorLine = null;

        this._circleDisc?.dispose();
        this._circleDisc = null;

        this._circleRing?.dispose();
        this._circleRing = null;

        this._circleMaterial?.dispose();
        this._circleMaterial = null;

        this._circleHoverMaterial?.dispose();
        this._circleHoverMaterial = null;

        if (this._utilityLayer) {
            this._utilityLayer.dispose();
            this._utilityLayer = null;
        }

        this._circleHovered = false;
    }
}
